import { fmtDate, fmtDateTime } from '@/lib/format-date';
import React from 'react';
import type { CrudConfig } from '@/components/shared/crud-page';
import { joinTitle } from '@/lib/detail-title';
import { WeightraxReviewCell, WEIGHTRAX_REVIEW_PATHS } from '@/components/shared/weightrax-review-cell';
import { CustomerImportDialog, exportForImport } from '@/components/shared/record-import-dialog';
import { toast } from '@/components/ui/toast';
import { customerLabel, COMPANY_MAX } from '@/components/shared/customer-picker';
import { IotEmailTemplatesSubform } from './iot-email-templates-subform';
import { EMPLOYEE_REF_QUERY } from '@/lib/ref-defaults';

export const CRUD_CONFIGS: Record<string, CrudConfig> = {
  companies: {
    title: 'Companies', singular: 'Company', endpoint: '/master-data/companies', formSlug: 'companies', pageSlug: 'companies_page',
    columns: [{ key: 'name', label: 'Name', filterType: 'text' }],
    fields: [{ name: 'name', label: 'Name', type: 'text', required: true }],
    relatedLists: [
      { title: 'Sites', endpoint: '/master-data/sites', filterField: 'company',
        columns: [{ key: 'name', label: 'Name' }] },
      { title: 'Service Locations', endpoint: '/master-data/service-locations', filterField: 'company',
        columns: [{ key: 'name', label: 'Name' }, { key: 'address.city', label: 'City' }] },
    ],
  },
  branches: {
    title: 'Branches', singular: 'Branch', endpoint: '/master-data/branches', formSlug: 'branches', pageSlug: 'branches_page',
    columns: [
      { key: 'name', label: 'Name', filterType: 'text' },
      { key: 'phone', label: 'Phone', filterType: 'text' },
      { key: 'emails', label: 'Emails', render: (r) => Array.isArray(r.emails) ? r.emails.length : 0 },
      { key: 'address.city', label: 'City' },
    ],
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'phone', label: 'Phone', type: 'text' },
      { name: 'emails', label: 'Emails', type: 'string-array', placeholder: 'name@example.com' },
      { name: 'address', label: 'Address', type: 'address' },
      // Multi-select of products this branch stocks, sourced from the
      // Products report (/catalogue-items). Items are managed from there;
      // here we just pick which subset is offered at this branch.
      { name: 'products', label: 'Products available', type: 'multi-ref',
        refEndpoint: '/catalogue-items', refLabel: 'itemCode' },
    ],
    relatedLists: [
      { title: 'Orders', endpoint: '/orders', filterField: 'branch',
        columns: [
          { key: 'orderNumber', label: 'Order #' },
          { key: 'dateOrdered', label: 'Date', render: (r) => fmtDate(r.dateOrdered) },
          { key: 'total', label: 'Total' },
          { key: 'status', label: 'Status' },
        ] },
      // The "Dispatch jobs (runsheet branch)" related list was removed
      // when `runsheetBranch` was dropped from DispatchJob — branch info
      // is now derived from `dispatchJob.asset.branch`. The generic CRUD
      // filter only accepts top-level paths, so a per-Branch dispatch-jobs
      // view would need a custom server endpoint; not worth re-adding for
      // a Zoho-leftover convenience list.
      { title: 'Run sheets', endpoint: '/run-sheets', filterField: 'branch',
        columns: [{ key: 'name', label: 'Name' }, { key: 'date', label: 'Date' }, { key: 'status', label: 'Status' }] },
    ],
  },
  sites: {
    title: 'Sites', singular: 'Site', endpoint: '/master-data/sites', formSlug: 'sites', pageSlug: 'sites_page',
    columns: [
      { key: 'name', label: 'Name', filterType: 'text' },
      { key: 'company.name', label: 'Company',
        filterType: 'text', filterField: 'company',
        render: (r) => r.company?.name || '—' },
      { key: 'weightraxSiteId', label: 'Weightrax',
        render: (r) => (r.weightraxSiteId
          ? `${r.weightraxSiteId}${r.weightraxRegCodeSet ? '' : ' (no reg code)'}`
          : '—') },
    ],
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'company', label: 'Company', type: 'ref', refEndpoint: '/master-data/companies', refLabel: 'name' },
      // Weightrax credential pair — set both or neither. The reg code is
      // stored encrypted and never sent back, so it always shows blank here;
      // leave it blank to keep the stored one. Clearing the site id clears both.
      { section: 'Weightrax', name: 'weightraxSiteId', label: 'Weightrax Site ID', type: 'number' },
      { section: 'Weightrax', name: 'weightraxSiteRegCode', label: 'Weightrax Reg Code (leave blank to keep)', type: 'text',
        placeholder: 'Stored encrypted — enter to set or replace' },
    ],
    validate: (v): Record<string, string> | null => (v.weightraxSiteRegCode && !v.weightraxSiteId
      ? { weightraxSiteId: 'Enter the Weightrax site id for this reg code' }
      : v.weightraxSiteId && !v.weightraxRegCodeSet && !v.weightraxSiteRegCode
        ? { weightraxSiteRegCode: 'Enter the Weightrax reg code for this site id' }
        : null),
  },
  'service-locations': {
    title: 'Service Locations', singular: 'Service Location', endpoint: '/master-data/service-locations', formSlug: 'service_locations', pageSlug: 'service_locations_page',
    columns: [
      { key: 'name', label: 'Name', filterType: 'text' },
      { key: 'address.city', label: 'City' },
    ],
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'company', label: 'Company', type: 'ref',
        refEndpoint: '/master-data/companies', refLabel: 'name' },
      { name: 'address', label: 'Address', type: 'address' },
    ],
    relatedLists: [
      { title: 'Employees in this location', endpoint: '/employees', filterField: 'serviceLocation',
        columns: [
          { key: 'fullName', label: 'Name' },
          { key: 'email', label: 'Email' },
        ] },
    ],
  },
  depots: {
    title: 'Depots', singular: 'Depot', endpoint: '/master-data/depots', formSlug: 'depots', pageSlug: 'depots_page',
    columns: [
      { key: 'code', label: 'Code', filterType: 'text' },
      { key: 'description', label: 'Description', filterType: 'text' },
      { key: 'contact.fullName', label: 'Contact',
        filterType: 'ref', filterField: 'contact',
        refEndpoint: '/employees', refLabel: 'fullName',
        render: (r) => r.contact?.fullName || '—' },
      { key: 'address.city', label: 'City' },
    ],
    fields: [
      { name: 'code', label: 'Code', type: 'text', required: true },
      { name: 'description', label: 'Description', type: 'textarea' },
      // Day-to-day contact for the depot. Filtered to active employees via
      // the shared EMPLOYEE_REF_QUERY so the picker doesn't surface archived
      // people. Server has the field + populate already wired.
      { name: 'contact', label: 'Contact', type: 'ref',
        refEndpoint: '/employees', refLabel: 'fullName',
        refQuery: EMPLOYEE_REF_QUERY },
      { name: 'address', label: 'Address', type: 'address' },
    ],
  },
  departments: {
    title: 'Departments', singular: 'Department', endpoint: '/master-data/departments', formSlug: 'departments', pageSlug: 'departments_page',
    columns: [{ key: 'name', label: 'Name', filterType: 'text' }],
    fields: [{ name: 'name', label: 'Name', type: 'text', required: true }],
    relatedLists: [
      { title: 'Assets in department', endpoint: '/fleet/assets', filterField: 'department',
        columns: [
          { key: 'fleetNumber', label: 'Fleet #' },
          { key: 'registrationNumber', label: 'Rego' },
          { key: 'make', label: 'Make' },
        ] },
      { title: 'Employees in department', endpoint: '/employees', filterField: 'department',
        columns: [
          { key: 'fullName', label: 'Name' },
          { key: 'email', label: 'Email' },
        ] },
    ],
  },
  customers: {
    title: 'Customers', singular: 'Customer', endpoint: '/master-data/customers', formSlug: 'customers', pageSlug: 'customers_page',
    detailTitle: (r) => (r.company
      ? customerLabel(r)
      : [r.contactName?.firstName, r.contactName?.lastName].filter(Boolean).join(' ') || r.email || 'Customer'),
    validate: (v): Record<string, string> | null => {
      const company = typeof v.company === 'string' ? v.company.trim() : '';
      if (!company) return { company: 'Company is required' };
      if (company.length > COMPANY_MAX) return { company: `Company must be ${COMPANY_MAX} characters or fewer` };
      if (!v.excludeFromWeightrax && !String(v.jdeCustomerCode ?? '').trim()) {
        return { jdeCustomerCode: 'Enter the JDE code, or exclude the customer from Weightrax' };
      }
      if (!v.excludeFromWeightrax && (!Array.isArray(v.sites) || v.sites.length === 0)) {
        return { sites: 'Pick at least one site, or exclude the customer from Weightrax' };
      }
      return null;
    },
    // The server hides archived customers unless asked, so the unfiltered
    // first tab is Active; All opts out of that default.
    tabs: [
      { label: 'Active' },
      // Its own card view — see pages/weightrax-review.
      { label: 'Needs review', to: WEIGHTRAX_REVIEW_PATHS.customer },
      { label: 'Archived', filter: { archived: 'true' } },
      { label: 'All', filter: { includeArchived: '1' } },
    ],
    columns: [
      { key: 'jdeCustomerCode', label: 'JDE Code', filterType: 'text' },
      { key: 'company', label: 'Company', filterType: 'text' },
      { key: 'archived', label: 'Status',
        render: (r) => (r.archived ? 'Archived' : 'Active') },
      { key: 'atlasAccountCode', label: 'Account', filterType: 'text' },
      // Push status of AFM-issued customers (legacy rows without a code aren't pushed).
      { key: 'sites', label: 'Sites',
        render: (r) => (Array.isArray(r.sites) && r.sites.length
          ? r.sites.map((s: any) => s?.name).filter(Boolean).join(', ')
          : '—') },
      // Push status across the customer's Weightrax sites (legacy rows
      // without a code aren't pushed).
      { key: 'weightraxSync', label: 'Weightrax',
        render: (r) => {
          const sync: any[] = Array.isArray(r.weightraxSync) ? r.weightraxSync : [];
          if (r.excludeFromWeightrax) return 'Excluded';
          if (!r.jdeCustomerCode) return 'Needs JDE code';
          if (sync.some((x) => x.error)) return 'Failed';
          if (sync.some((x) => x.linkedExisting)) return r.archived ? 'Linked — needs review' : React.createElement(WeightraxReviewCell, { kind: 'customer', row: r });
          return sync.length && sync.every((x) => x.customerId) ? 'Synced' : 'Pending';
        } },
      { key: 'email', label: 'Email', filterType: 'text' },
      { key: 'mobile', label: 'Mobile', filterType: 'text' },
    ],
    fields: [
      { name: 'company', label: 'Company', type: 'text', required: true },
      // Excluded customers are never pushed to Weightrax and don't need a Site.
      { name: 'excludeFromWeightrax', label: 'Exclude from Weightrax', type: 'boolean' },
      // From JDE (typed here or CSV-imported) — AFM never generates it.
      { name: 'jdeCustomerCode', label: 'JDE Code (required unless excluded from Weightrax)', type: 'text',
        placeholder: 'e.g. C41808' },
      { name: 'sites', label: 'Sites (required unless excluded from Weightrax)', type: 'multi-ref',
        refEndpoint: '/master-data/sites', refLabel: 'name' },
      { name: 'atlasAccountCode', label: 'Atlas Account Code', type: 'text' },
      { name: 'contactName', label: 'Contact Name', type: 'contact' },
      { name: 'email', label: 'Email', type: 'email' },
      { name: 'mobile', label: 'Mobile', type: 'text' },
      { name: 'role', label: 'Role', type: 'text' },
      { name: 'address', label: 'Postal Address', type: 'address' },
    ],
    // Round trip for bulk edits (e.g. JDE codes): export → edit in Excel →
    // import with a preview. See components/shared/record-import-dialog.
    toolbarActions: [
      { label: 'Export for import', variant: 'outline',
        onClick: async () => { try { await exportForImport('customer'); } catch (e: any) { toast('Export failed', e.message, 'error'); } } },
      { label: 'Import CSV', variant: 'outline', customDialog: CustomerImportDialog },
    ],
    rowActions: [
      // Customers on jobs/orders can't be deleted (server refuses) — archive
      // hides them from Customer Search and marks them obsolete in Weightrax.
      { label: 'Archive', endpoint: '/master-data/customers/:id/archive', method: 'POST',
        confirm: 'Archive this customer? It will be hidden from Customer Search and marked obsolete in Weightrax. Existing jobs keep it.',
        visible: (r) => !r.archived },
      { label: 'Restore', endpoint: '/master-data/customers/:id/unarchive', method: 'POST',
        visible: (r) => !!r.archived },
      { label: 'Push to Weightrax', endpoint: '/master-data/customers/:id/weightrax-sync', method: 'POST',
        visible: (r) => !!r.jdeCustomerCode && !r.excludeFromWeightrax && !r.archived
          && (!r.weightraxSync?.length || r.weightraxSync.some((x: any) => x.error || !x.customerId)) },
    ],
    relatedLists: [
      { title: 'Orders', endpoint: '/orders', filterField: 'customer',
        columns: [
          { key: 'orderNumber', label: 'Order #' },
          { key: 'dateOrdered', label: 'Date', render: (r) => fmtDate(r.dateOrdered) },
          { key: 'total', label: 'Total' },
          { key: 'status', label: 'Status' },
        ] },
    ],
  },
  vendors: {
    title: 'Vendors', singular: 'Vendor', endpoint: '/master-data/vendors', formSlug: 'vendors', pageSlug: 'vendors_page',
    columns: [
      { key: 'name', label: 'Name', filterType: 'text' },
      { key: 'vendorType', label: 'Type', filterType: 'enum',
        enumOptions: [{ value: 'Fuel', label: 'Fuel' }, { value: 'Service', label: 'Service' }] },
      { key: 'status', label: 'Status', filterType: 'enum',
        enumOptions: [{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }] },
      { key: 'email', label: 'Email', filterType: 'text' },
    ],
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true },
      {
        name: 'status', label: 'Status', type: 'select', required: true,
        options: [{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }],
      },
      {
        name: 'vendorType', label: 'Vendor Type', type: 'select',
        options: [{ value: 'Fuel', label: 'Fuel' }, { value: 'Service', label: 'Service' }],
      },
      { name: 'contactPerson', label: 'Contact Person', type: 'contact' },
      { name: 'phone', label: 'Phone', type: 'text' },
      { name: 'email', label: 'Email', type: 'email' },
      { name: 'website', label: 'Website', type: 'text' },
      { name: 'address', label: 'Address', type: 'address' },
    ],
  },
  'vehicle-groups': {
    title: 'Vehicle Groups', singular: 'Vehicle Group', endpoint: '/master-data/vehicle-groups', formSlug: 'vehicle_groups', pageSlug: 'vehicle_groups_page',
    detailTitle: (r) => joinTitle(r.group),
    columns: [{ key: 'group', label: 'Group', filterType: 'text' }],
    fields: [{ name: 'group', label: 'Group', type: 'text', required: true }],
    relatedLists: [
      { title: 'Assets in group', endpoint: '/fleet/assets', filterField: 'vehicleGroup',
        columns: [
          { key: 'fleetNumber', label: 'Fleet #' },
          { key: 'registrationNumber', label: 'Rego' },
          { key: 'make', label: 'Make' },
        ] },
      { title: 'Assets in group', endpoint: '/fleet/assets', filterField: 'vehicleGroup',
        columns: [
          { key: 'fleetNumber', label: 'Fleet #' },
          { key: 'registrationNumber', label: 'Rego' },
        ] },
    ],
  },
  'vehicle-types': {
    title: 'Vehicle Types', singular: 'Vehicle Type', endpoint: '/master-data/vehicle-types', formSlug: 'vehicle_types', pageSlug: 'vehicle_types_page',
    detailTitle: (r) => joinTitle(r.type),
    columns: [
      { key: 'type', label: 'Type', filterType: 'text' },
      { key: 'meterType', label: 'Meter', filterType: 'enum',
        enumOptions: [
          { value: 'Odometer', label: 'Odometer' },
          { value: 'Hubo', label: 'Hubo' },
          { value: 'Engine Hours', label: 'Engine Hours' },
        ] },
      { key: 'count', label: 'Count', filterType: 'number' },
    ],
    fields: [
      { name: 'type', label: 'Type', type: 'text', required: true },
      {
        name: 'meterType', label: 'Meter Type', type: 'select',
        options: [
          { value: 'Odometer', label: 'Odometer' },
          { value: 'Hubo', label: 'Hubo' },
          { value: 'Engine Hours', label: 'Engine Hours' },
        ],
      },
    ],
    relatedLists: [
      { title: 'Assets of type', endpoint: '/fleet/assets', filterField: 'vehicleType',
        columns: [
          { key: 'fleetNumber', label: 'Fleet #' },
          { key: 'registrationNumber', label: 'Rego' },
          { key: 'make', label: 'Make' },
        ] },
    ],
  },
  units: {
    title: 'Units', singular: 'Unit', endpoint: '/master-data/units', formSlug: 'units', pageSlug: 'units_page',
    columns: [{ key: 'name', label: 'Name', filterType: 'text' }],
    fields: [{ name: 'name', label: 'Name', type: 'text', required: true }],
    relatedLists: [
      { title: 'Materials using this unit', endpoint: '/materials', filterField: 'unit',
        columns: [{ key: 'materialName', label: 'Name' }, { key: 'cost', label: 'Cost' }] },
    ],
  },
  // Job Codes report removed from the admin UI. The endpoint
  // (/master-data/job-codes) is still reachable for ref pickers
  // (ServiceHistory.jobCode etc.) and migrated records preserve their
  // values, but the standalone CRUD report is no longer surfaced.
  'dispatch-groups': {
    title: 'Dispatch Groups', singular: 'Dispatch Group', endpoint: '/master-data/dispatch-groups',
    formSlug: 'dispatch_groups', pageSlug: 'dispatch_groups_page',
    columns: [
      { key: 'name', label: 'Name', filterType: 'text' },
      { key: 'description', label: 'Description', filterType: 'text' },
      { key: 'sortOrder', label: 'Sort', filterType: 'number' },
    ],
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'description', label: 'Description', type: 'textarea' },
      { name: 'sortOrder', label: 'Sort Order', type: 'number' },
    ],
  },
  'iot-suppliers': {
    title: 'IoT Suppliers', singular: 'IoT Supplier', endpoint: '/master-data/iot-suppliers', formSlug: 'iot_suppliers', pageSlug: 'iot_suppliers_page',
    detailTitle: (r) => joinTitle(r.providerName),
    columns: [
      { key: 'providerName', label: 'Provider', filterType: 'text' },
      { key: 'email', label: 'Email', filterType: 'text' },
      { key: 'phone', label: 'Phone', filterType: 'text' },
      // Onboard / offboard email status: derived from "has recipients"
      // rather than a separate enabled flag. Empty recipients = no send
      // will happen (the iot-email service treats absent recipients as
      // the implicit off switch).
      { key: 'onboardTemplate.recipients', label: 'Onboard email',
        render: (r) => (r.onboardTemplate?.recipients ?? '').trim() ? 'Configured' : 'Off' },
      { key: 'offboardTemplate.recipients', label: 'Offboard email',
        render: (r) => (r.offboardTemplate?.recipients ?? '').trim() ? 'Configured' : 'Off' },
    ],
    fields: [
      { name: 'providerName', label: 'Provider Name', type: 'text', required: true },
      { name: 'email', label: 'Email', type: 'email' },
      { name: 'phone', label: 'Phone', type: 'text' },
    ],
    // Email templates render as a custom subform under the basic fields so the
    // placeholder helper + Onboard/Offboard cards stay grouped visually.
    FormExtras: IotEmailTemplatesSubform,
    // The two embedded template sub-docs aren't in `fields` — they're managed
    // by FormExtras; pull them through the submit body explicitly.
    extraSaveKeys: ['onboardTemplate', 'offboardTemplate'],
  },
  'authorized-emails': {
    title: 'Authorized Emails', singular: 'Authorized Email', endpoint: '/master-data/authorized-emails', formSlug: 'authorized_emails', pageSlug: 'authorized_emails_page',
    detailTitle: (r) => joinTitle(r.email),
    columns: [{ key: 'email', label: 'Email', filterType: 'text' }],
    fields: [{ name: 'email', label: 'Email', type: 'email', required: true }],
  },
  'app-settings': {
    title: 'App Settings', singular: 'Setting', endpoint: '/master-data/app-settings',
    detailTitle: (r) => joinTitle(r.key),
    columns: [
      { key: 'key', label: 'Key', filterType: 'text' },
      { key: 'value', label: 'Value', render: (r) => React.createElement('code', { className: 'text-xs' }, JSON.stringify(r.value)) },
    ],
    fields: [
      { name: 'key', label: 'Key', type: 'text', required: true },
      { name: 'value', label: 'Value', type: 'text' },
    ],
  },
};
